import React, { useState, useEffect, useContext, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useLocation, useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import MainTemplate from "../templates/MainTemplate"
import { Button, List, Typography } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import CaseList from './CaseList'
import { GlobalContext } from '../../hooks/reducer'
import { actions } from "../../hooks/useLoginUserReducer"
import { actions as shareCaseActions } from "../../hooks/useShereCaseReducer"
import { actions as infoActions } from "../../hooks/useInfoReducer"
import { useLiveVisibleCases } from '../../hooks/useLiveVisibleCases'
import CustomModal from "../common/CustomModal"
import PageTitle from '../common/PageTitle'
import PageSizeSelector from './common/PageSizeSelector'
import * as Scroll from 'react-scroll'
import FixedInfoList from '../info/FixedInfoList'

// 時間の設定
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')

const getStyles = makeStyles((theme) => ({
  list: {
    marginTop: '-50px'
  },
  infoWrap: {
    padding: '20px 20px 24px',
    backgroundColor: 'rgb(255 245 228)',
    borderRadius: '4px',
    marginBottom: '40px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 14px',
    },
  },
  cursorPager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '30px',
  },
}))

const ShareCase = React.memo((props) => {
  const classes = getStyles()
  const history = useHistory()
  const location = useLocation()

  const { state: { shareCase, loginUser, common, admin, info }, dispatch } = useContext(GlobalContext)
  const {
    filterdCases, cases, inputComments, caseComments,
    industryTagList, memberNumTagList, prefTagList, feedbackCaseTag, otherTagList
  } = shareCase
  const { searchTag } = common
  const { importantInfoKey } = admin
  const { importantInfo } = info

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [pageCases, setPageCases] = useState([])
  const [hasNextPage, setHasNextPage] = useState(false)
  const [pageCursors, setPageCursors] = useState([null])
  const [isLoadingCases, setIsLoadingCases] = useState(true)
  const startNum = (page - 1) * perPage
  const isSearchMode = !!searchTag
  const totalPages = isSearchMode ? Math.ceil(filterdCases.length / perPage) : 0
  const currentCursor = pageCursors[page - 1] || null
  // 通常一覧はサーバー側ページ取得、検索時だけ従来どおりクライアント側ページングを使う。
  const baseVisibleCases = useMemo(
    () => isSearchMode ? filterdCases.slice(startNum, startNum + perPage) : pageCases,
    [filterdCases, isSearchMode, pageCases, startNum]
  )
  const visibleCases = useLiveVisibleCases(baseVisibleCases, loginUser.isLogin)
  const commentsByCaseKey = useMemo(() => {
    return caseComments.reduce((acc, comment) => {
      if (!acc[comment.caseKey]) acc[comment.caseKey] = []
      acc[comment.caseKey].push(comment)
      return acc
    }, {})
  }, [caseComments])

  // 初回mount
  useEffect(() => {
    // ログインしていない場合、ログインページに遷移
    if (!loginUser.isLogin) { actions.loginCheck(dispatch, history) }
    if (loginUser.isLogin) {
      shareCaseActions.getMasterData(dispatch)
      shareCaseActions.getIndustries(dispatch)
      shareCaseActions.getMemberNums(dispatch)
      shareCaseActions.getPrefs(dispatch)
      shareCaseActions.getOtherTags(dispatch)
    }
    return () => { 
      dispatch({ type: 'CASE_RESET' })
      dispatch({ type: 'COMMON_RESET' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, loginUser.isLogin])

  useEffect(() => {
    infoActions.getImportantInfo(dispatch, importantInfoKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, importantInfoKey])

  // NOTE: 検索切り替え時はページ位置を先頭に戻す。
  useEffect(() => {
    setPage(1)

    if (!searchTag) {
      setPageCases([])
      setHasNextPage(false)
      setPageCursors([null])
      dispatch({ type: 'SET_CASES', data: [] })
      dispatch({ type: 'SET_FILERED_CASES', data: [] })
    }
  }, [dispatch, searchTag])

  useEffect(() => {
    if (!loginUser.isLogin || !isSearchMode || cases.length > 0) return

    setIsLoadingCases(true)

    // 検索は現状クライアント側絞り込みのため、検索開始時だけ全件を読み込む。
    shareCaseActions.getCases(dispatch)
      .finally(() => setIsLoadingCases(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases.length, dispatch, isSearchMode, loginUser.isLogin])

  useEffect(() => {
    if (!isSearchMode) return

    console.log("check search")
    shareCaseActions.filterCases(dispatch, searchTag, cases, {
      industryTagList, memberNumTagList, prefTagList, feedbackCaseTag, otherTagList
    })
    const scroll = Scroll.animateScroll
    scroll.scrollToTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, dispatch, feedbackCaseTag, industryTagList, isSearchMode, memberNumTagList, otherTagList, prefTagList, searchTag])

  useEffect(() => {
    if (!loginUser.isLogin || isSearchMode) return undefined

    let cancelled = false
    setIsLoadingCases(true)

    shareCaseActions.getCasesPage({ perPage, cursorKey: currentCursor })
      .then(({ cases: nextCases, hasNext, nextCursor }) => {
        if (cancelled) return

        setPageCases(nextCases)
        setHasNextPage(hasNext)
        setPageCursors((prev) => {
          const next = prev.slice(0, page)

          if (hasNext && nextCursor) {
            next[page] = nextCursor
          }

          return next
        })
      })
      .catch((error) => {
        if (cancelled) return
        console.error('getCasesPage error', error)
        setPageCases([])
        setHasNextPage(false)
      })
      .finally(() => {
        if (cancelled) return
        setIsLoadingCases(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCursor, dispatch, isSearchMode, loginUser.isLogin, page, perPage])

  useEffect(() => {
    const scroll = Scroll.animateScroll
    scroll.scrollToTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    if (!loginUser.isLogin) return undefined

    const visibleCaseKeys = visibleCases.map((content) => content.key)
    // コメントといいねは visible case だけを追い、全件購読を避ける。
    const cleanupComments = shareCaseActions.watchVisibleComments(dispatch, visibleCaseKeys)
    const cleanupGoods = shareCaseActions.watchVisibleGoods(dispatch, loginUser.user.uid, visibleCaseKeys)

    return () => {
      cleanupComments()
      cleanupGoods()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loginUser.isLogin, loginUser.user.uid, visibleCases])

  const handleBackToLatest = () => {
    setPageCases([])
    setHasNextPage(false)
    setPageCursors([null])
    setPage(1)
  }

  const handlePerPageChange = (nextPerPage) => {
    if (nextPerPage === perPage) return

    setPerPage(nextPerPage)
    setPage(1)
    setPageCases([])
    setHasNextPage(false)
    setPageCursors([null])
  }

  const renderPager = () => {
    if (isSearchMode) {
      if (filterdCases.length <= 0) return null

      return (
        <Pagination
          count={totalPages}
          size="large"
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          style={{ marginBottom: '30px', position: 'relative', zIndex: 1 }}
        />
      )
    }

    if (pageCases.length <= 0 || isLoadingCases) return null

    return (
      <div className={classes.cursorPager}>
        <Button
          variant="outlined"
          color="primary"
          disabled={page <= 1}
          onClick={() => setPage((prevPage) => Math.max(1, prevPage - 1))}
        >
          前へ
        </Button>

        <Typography variant="body2">
          {page}ページ目
        </Typography>

        <Button
          variant="contained"
          color="primary"
          disabled={!hasNextPage}
          onClick={() => setPage((prevPage) => prevPage + 1)}
        >
          次へ
        </Button>

        <Button
          variant="text"
          color="primary"
          disabled={page <= 1}
          onClick={handleBackToLatest}
        >
          最新へ戻る
        </Button>
      </div>
    )
  }

  return (
    <MainTemplate>

      {/* モーダル */}
      <CustomModal />

      {/* 重要なお知らせ */}
      {
        !!importantInfo && <List className={classes.infoWrap}>
          <Typography
            variant='body1'
            component='li'
            align='left'
            style={{
              fontWeight: 'bold',
              marginBottom: '10px',
            }}
          >
            重要なお知らせ
          </Typography>

          <FixedInfoList
            InfoKey={importantInfoKey}
            content={importantInfo}
            handleDispatch={(e) => dispatch(e)}
            customStyle='importantInfo'
          />
        </List>
      }

      <PageTitle title='みんなの事例' />

      {
        searchTag && <Typography variant='body1' component='p' align='center' style={{ marginBottom: '30px', marginRight: '10px' }}>
          「 {searchTag.label} 」の検索結果一覧
        </Typography>
      }

      <PageSizeSelector perPage={perPage} onChange={handlePerPageChange} />

      {renderPager()}

      <List className={classes.list}>
        <React.Fragment>
          {
            visibleCases.length <= 0 ? (
              isLoadingCases ? '読み込み中...' : (isSearchMode ? '検索結果は0件です。' : '事例がありません。')
            ) : (
              /** NOTE:
               * map((content,key)だと 0〜4 の key が再利用され、
               * React は「同じ要素だ」と誤認→子の内部状態（Vimeo の uploaded/loadError/Player インスタンス）を他の行に持ち回りします。
               * → 必ず安定IDを使う。
               */
              visibleCases.map((content) => {
                const comments = commentsByCaseKey[content.key] || []
                return <CaseList
                  hash={location.hash}
                  key={content.key}
                  content={content}
                  inputComment={inputComments[content.key]}
                  comments={comments}
                  handleDispatch={(e) => dispatch(e)}
                />
              })
            )
          }
        </React.Fragment>
      </List>

      {renderPager()}

    </MainTemplate >
  )
})
export default ShareCase
